import { Injectable, effect, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { AppStore } from '../store';
import { DemoPersonaSwitchService } from '../demo/demo-persona-switch.service';

/**
 * Embed producer — the SPA half of the townsim⇄CashBro embed contract v1
 * (notes/spa-embed-contract.md, pinned df2dc530, cadence-green-lit).
 *
 * URL contract (read ONCE at bootstrap; presentation-only — never alters data access):
 *   ?embed=1            chrome-free render (body.tb-embed hides topbar/nav via styles.scss)
 *   &persona=<key>      applied via the EXISTING in-place switch once the session resolves
 *                       (personas are acting contexts, never logins — ADR-0106)
 *
 * postMessage contract (iframe → parent, ONE-WAY in v1; inbound control is explicitly deferred
 * to v2 behind the challenge-code design):
 *   { source: 'toybank-spa', v: 1, type: 'ready'|'route-changed'|'persona-changed'|'session-required', ... }
 *
 * SECURITY: messages go ONLY to the EMBED_ALLOWED_ORIGINS allowlist (never '*'); they carry
 * route + persona KEY only — no tokens, no session ids, no account data (the session stays
 * cookie-bound inside the iframe, ADR-0003). embed=1 grants nothing: every request still rides
 * the BFF session; the server remains the enforcer.
 */
@Injectable({ providedIn: 'root' })
export class EmbedService {
  /**
   * Build-time embedding-origin allowlist (the townsim viewer hosts). The SPA has no runtime
   * flag service (ADR-0076 Flags note), so this ships as a constant for the demo configuration.
   * The production customer host stays non-framable via CSP regardless of this list.
   */
  static readonly EMBED_ALLOWED_ORIGINS: readonly string[] = [
    'http://town.localhost',
    'http://demo.localhost',
    'http://debug.demo.localhost',
    // Public-demo twins (Cloudflare Tunnel, 2026-09-23): same hosts one level under chocolatepi.net.
    'https://demo.chocolatepi.net',
    'https://debug.demo.chocolatepi.net',
    'https://sim.chocolatepi.net',
    'https://debug.sim.chocolatepi.net',
  ];

  private readonly router = inject(Router);
  private readonly store = inject(AppStore);
  private readonly demoPersonaSwitcher = inject(DemoPersonaSwitchService);

  /** True when this document was booted with ?embed=1 (read once; reload changes it). */
  readonly isEmbed: boolean;
  /** The requested acting persona from ?persona= (applied once the session resolves). */
  private readonly requestedPersona: string | null;
  /** True when the demo switcher accepted the requested persona into its still-locked bootstrap. */
  private readonly bootstrapOwnsRequestedPersona: boolean;

  private readyEmitted = false;
  private latestRoute: string | null = null;
  private personaSwitchAttempted = false;
  private personaSwitchSettled = false;
  private personaChangedEmitted = false;
  private sessionRequiredEmitted = false;

  constructor() {
    const params = new URLSearchParams(window.location.search);
    this.isEmbed = params.get('embed') === '1';
    this.requestedPersona = params.get('persona')?.trim() || null;
    this.bootstrapOwnsRequestedPersona = !!(
      this.isEmbed
      && this.requestedPersona
      && this.demoPersonaSwitcher.configureBootstrapPersona(this.requestedPersona)
    );

    if (!this.isEmbed) return;
    document.body.classList.add('tb-embed');

    this.router.events.pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((e) => {
        const route = e.urlAfterRedirects.split('?')[0];
        this.latestRoute = route;
        if (this.readyEmitted) {
          this.post('route-changed', { route });
        } else {
          this.advanceReadiness();
        }
        if (route.startsWith('/signed-out') || route.startsWith('/login')) {
          this.emitSessionRequired();
        }
      });

    // CONTRACT-FLAGGED RACES (v1 §URL/persona) — all are SESSION-STATE-driven, not sampled:
    //  1. persona-before-bootstrap: neither READY nor the requested persona hop may happen until
    //     DemoPersonaSwitchService has verified the server-held acting context via /api/me.
    //  2. persona-before-session: a login completing inside the iframe applies the requested key
    //     late, exactly once, through the shared switcher (never an independent HTTP path).
    //  3. session-required without a route change: the debug/demo wrapper re-shows its picker at
    //     the SAME url, so route inference alone misses it. When the bootstrap session check
    //     completes ABSENT, tell the parent — once per absence episode (a later login resets it).
    effect(() => {
      const user = this.store.user?.();
      const checked = this.store.sessionChecked?.() ?? false;
      // Read every switcher state that can advance the embed handshake. The body delegates to a
      // single transition function so router events and signal changes share the same ordering.
      this.demoPersonaSwitcher.bootstrapReady();
      this.demoPersonaSwitcher.activeKey();
      this.demoPersonaSwitcher.switching();
      this.demoPersonaSwitcher.error();
      if (user) {
        this.sessionRequiredEmitted = false; // new absence episodes may signal again
      } else if (checked) {
        this.emitSessionRequired();
      }
      this.advanceReadiness();
    });
  }

  /**
   * Advance the one-way embed handshake. READY is possible only after demo bootstrap has verified
   * the acting context and, when requested, the shared persona switch has settled. The switcher is
   * the sole owner of POST + session metadata + /api/me refresh; this service only observes it.
   */
  private advanceReadiness(): void {
    if (this.readyEmitted || !this.demoPersonaSwitcher.bootstrapReady()) return;

    const activeKey = this.demoPersonaSwitcher.activeKey();
    if (this.requestedPersona && !this.personaSwitchSettled) {
      // On demo hosts the shared switcher can absorb the requested key into its initial locked
      // bootstrap. `bootstrapReady` then means target identity + core hydration have both settled;
      // never issue a second mutation from the embed observer.
      if (this.bootstrapOwnsRequestedPersona) {
        this.personaSwitchSettled = true;
      } else {
        // A persona parameter can never mint a session. Keep the normal login flow and wait for its
        // live session; session-required remains the parent-facing hint in the meantime.
        if (!this.store.user?.()) return;

        const switching = this.demoPersonaSwitcher.switching();
        const switchError = this.demoPersonaSwitcher.error();
        if (this.samePersona(activeKey, this.requestedPersona) && !switching) {
          this.settlePersonaSwitch(activeKey, this.personaSwitchAttempted);
        } else if (switching) {
          return;
        } else if (!this.personaSwitchAttempted) {
          this.personaSwitchAttempted = true;
          this.demoPersonaSwitcher.switchTo(this.requestedPersona);

          // switchTo is synchronous at its boundary: a valid target marks `switching` before its
          // HTTP request, while an invalid/unavailable key is a no-op. Re-read the SSOT signals so
          // an invalid target settles fail-soft now instead of waiting for a signal that never moves.
          const activeAfter = this.demoPersonaSwitcher.activeKey();
          const switchingAfter = this.demoPersonaSwitcher.switching();
          if (this.samePersona(activeAfter, this.requestedPersona) && !switchingAfter) {
            this.settlePersonaSwitch(activeAfter, true);
          } else if (!switchingAfter) {
            this.personaSwitchSettled = true;
          } else {
            return;
          }
        } else if (switchError) {
          // The shared switcher reported a failed request: continue with the actual active persona.
          // This is a one-way handshake, so failure settles once and never creates a retry loop.
          this.personaSwitchSettled = true;
        } else {
          // A completed/no-op attempt with no active target and no reported transport error is the
          // invalid-key face. It also settles once; switchTo remains the validator/SSOT.
          this.personaSwitchSettled = true;
        }
      }
    }

    if (this.requestedPersona && !this.personaSwitchSettled) return;
    if (!this.latestRoute) return;

    this.readyEmitted = true;
    this.post('ready', {
      route: this.latestRoute,
      personaKey: this.demoPersonaSwitcher.activeKey(),
    });
  }

  private settlePersonaSwitch(activeKey: string | null, emitChanged: boolean): void {
    this.personaSwitchSettled = true;
    if (emitChanged && activeKey && !this.personaChangedEmitted) {
      this.personaChangedEmitted = true;
      this.post('persona-changed', { personaKey: activeKey });
    }
  }

  private samePersona(left: string | null, right: string | null): boolean {
    return !!left && !!right && left.trim().toLowerCase() === right.trim().toLowerCase();
  }

  private emitSessionRequired(): void {
    if (this.sessionRequiredEmitted) return;
    this.sessionRequiredEmitted = true;
    this.post('session-required', {});
  }

  /** Envelope + allowlisted fan-out. NEVER '*'; silent no-op outside an iframe. */
  private post(type: string, fields: Record<string, unknown>): void {
    if (window.parent === window) return;
    const message = { source: 'toybank-spa', v: 1, type, ...fields };
    for (const origin of EmbedService.EMBED_ALLOWED_ORIGINS) {
      try {
        window.parent.postMessage(message, origin);
      } catch {
        // A non-matching targetOrigin throws in some engines — allowlist fan-out is best-effort
        // by design; the matching origin receives it.
      }
    }
  }
}
